// Sale Notes — the whole direct-sales register, not just the supervisors'.
//
// Two kinds of record share this book and they are different things:
//   · a supervisor's NOTE — "I sold this, put it in the day book" (pending →
//     entered workflow, one amount, parts without prices)
//   · a COUNTER SALE — the book entry itself (the walk-in team's night typing
//     or the admin's own direct sale; items with real prices, payment state)
//
// The register is a LIST, not a wall of cards: hundreds of customers will
// pass through here, so each record is one scannable row — date, who, what,
// how much, where it stands — with view / edit / delete on the row. The full
// story lives in the View popup, which scrolls INSIDE itself (a ten-product
// bill must never outgrow the screen) and names every field, writing "—"
// where the supervisor simply didn't note something.
import React, { useCallback, useEffect, useState } from "react";
import {
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiEdit3,
  FiEye,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import MainLayout from "../components/layout/MainLayout.jsx";
import { DeleteConfirm } from "../components/common/index.js";
// The same one-box car picker, type-or-pick input and styled dropdown the
// rest of the panel uses — a car is found by typing "swi", not by two
// dropdowns, and a chooser looks the same here as everywhere else. The
// browser's native <select> cannot be styled to match, so it is not used.
import { CarPicker, ComboInput, Select } from "../components/cars/controls.jsx";
import { saleNotesApi } from "../api/saleNotesApi";
import { saleRemindersApi } from "../api/saleRemindersApi";
import { counterApi } from "../api/opsApi";
import { activeExchangeNames, isExchangeEligible } from "../utils/exchangeEligibility";
import { PAYMENT_MODE_OPTIONS, modeLabel } from "../constants/paymentModes";

const money = (value) =>
  value != null && value !== ""
    ? `₹${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "—";

const errText = (e, fallback) => {
  const d = e?.response?.data;
  if (!d || typeof d === "string") return fallback;
  if (d.error) return d.error;
  const k = Object.keys(d)[0];
  const v = Array.isArray(d[k]) ? d[k][0] : d[k];
  return typeof v === "string" ? `${k}: ${v}` : fallback;
};

const WINDOWS = [
  { value: "all", label: "Overall" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "custom", label: "Custom" },
];

const KINDS = [
  { value: "all", label: "All records" },
  { value: "supervisor", label: "Supervisor notes" },
  { value: "walkin", label: "Walk-in book" },
  { value: "personal", label: "Admin direct" },
];

// ── Codes → words. The APIs speak in codes (dmt, gpay, cap); a popup that
// shows the raw code reads as a bug to the person using it. One map each,
// tolerant of free text: an unknown value passes through rather than hiding.
const VARIANT_LABELS = {
  pmt: "Petrol Manual",
  pat: "Petrol Automatic",
  dmt: "Diesel Manual",
  dat: "Diesel Automatic",
  ev: "Electric (EV)",
};
const variantLabel = (v) => VARIANT_LABELS[String(v || "").toLowerCase()] || v || "";

// Payment modes come from the one shared list — this screen used to carry
// its own five, which is how a Paytm note rendered as an empty control.

// 'stock' is the outside-parts shelf — spelt out, since a bare "STOCK" next
// to CAP and OSM means nothing at a glance.
const SALE_SOURCE_LABELS = { cap: "CAP", osm: "OSM", stock: "Outside Parts" };
const sourceLabel = (s) => SALE_SOURCE_LABELS[String(s || "").toLowerCase()] || (s ? String(s).toUpperCase() : "");

const KIND_BADGES = {
  supervisor: { label: "Note", cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  walkin: { label: "Walk-in", cls: "bg-sky-50 text-sky-700 ring-sky-200" },
  personal: { label: "Direct", cls: "bg-violet-50 text-violet-700 ring-violet-200" },
};

// Timestamps arrive in two dialects: notes send raw ISO, counter sales send
// text already formatted by the backend. Parse what parses; pass through
// what doesn't — never show "Invalid Date".
const fmtStamp = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
  });
};

const VARIANT_OPTIONS = [
  { value: "", label: "Variant not set" },
  { value: "pmt", label: "Petrol Manual" },
  { value: "pat", label: "Petrol Automatic" },
  { value: "dmt", label: "Diesel Manual" },
  { value: "dat", label: "Diesel Automatic" },
  { value: "ev", label: "Electric (EV)" },
];

// A note may genuinely not record how it was paid, so '' is a real choice here.
const PAY_MODES = [{ value: "", label: "Mode not set" }, ...PAYMENT_MODE_OPTIONS];

const SOURCE_OPTIONS = [
  { value: "cap", label: "CAP" },
  { value: "osm", label: "OSM" },
  { value: "stock", label: "Outside Parts" },
];

// Same span as the car form: next year down to 36 back.
const YEARS = Array.from({ length: 36 }, (_, i) => String(new Date().getFullYear() + 1 - i));

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-100";

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">
      {label}
    </span>
    {children}
  </label>
);


// ── The modal shell — scroll-safe by construction ────────────────────────────
// Header and footer stay put; ONLY the body scrolls (max-h caps the panel at
// the viewport). The old shell centred the whole panel and let it outgrow the
// screen — a tall bill clipped its own top edge beyond reach, which is the
// "can't scroll inside" bug. Never center a panel that can be taller than
// the screen without an internal scroll area.
const Modal = ({ title, subtitle, onClose, children, footer }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-3 backdrop-blur-sm sm:p-6"
    onClick={onClose}
  >
    <div
      className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-3xl bg-white shadow-2xl"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
        <div className="min-w-0">
          <h2 className="text-lg font-black text-slate-900">{title}</h2>
          {subtitle && <p className="mt-0.5 truncate text-sm text-slate-500">{subtitle}</p>}
        </div>
        <button
          onClick={onClose}
          className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close"
        >
          <FiX className="h-5 w-5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
      {footer && (
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
          {footer}
        </div>
      )}
    </div>
  </div>
);

// ── The View popup ───────────────────────────────────────────────────────────
// Every field the endpoint returns, named, in reading order. An empty value
// prints as "—": the supervisor not noting the car year is a fact worth
// showing plainly, not a blank that looks like the screen is broken.

const DetailRow = ({ label, value, strong }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <span className="shrink-0 text-xs font-bold uppercase tracking-wide text-slate-400">{label}</span>
    <span className={`min-w-0 text-right text-sm ${strong ? "font-black text-slate-900" : "font-semibold text-slate-700"}`}>
      {value === null || value === undefined || value === "" ? "—" : value}
    </span>
  </div>
);

const SectionTitle = ({ children }) => (
  <p className="mb-1 mt-5 text-[11px] font-black uppercase tracking-widest text-violet-500 first:mt-0">
    {children}
  </p>
);

const StatusChip = ({ tone, children }) => {
  const tones = {
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    slate: "bg-slate-100 text-slate-600 ring-slate-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ring-1 ${tones[tone]}`}>
      {children}
    </span>
  );
};

const ViewModal = ({ item, onClose, onEdit, onMark, busy }) => {
  const isNote = item.kind === "supervisor";
  const badge = KIND_BADGES[item.kind] || KIND_BADGES.supervisor;
  const car = [item.car_brand, item.car_model].filter(Boolean).join(" ");

  return (
    <Modal
      title={isNote ? "Supervisor sale note" : (item.kind === "walkin" ? "Walk-in book entry" : "Admin direct sale")}
      subtitle={`${isNote ? item.customer_name || "Customer" : item.customer?.name || "Customer"} · ${isNote ? item.sale_date : item.sold_at}`}
      onClose={onClose}
      footer={
        <>
          {isNote && item.status === "pending" && (
            <button
              onClick={() => onMark(item, "entered")}
              disabled={busy}
              className="mr-auto inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs font-black text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              <FiCheck /> Mark entered
            </button>
          )}
          {isNote && item.status === "entered" && (
            <button
              onClick={() => onMark(item, "pending")}
              disabled={busy}
              className="mr-auto text-xs font-bold text-slate-500 hover:text-violet-700"
            >
              Undo entry
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
          <button
            onClick={() => onEdit(item)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-black text-white hover:bg-violet-700"
          >
            <FiEdit3 className="h-4 w-4" /> Edit
          </button>
        </>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ring-1 ${badge.cls}`}>
          {badge.label}
        </span>
        {isNote ? (
          item.status === "pending"
            ? <StatusChip tone="amber">Needs entry</StatusChip>
            : <StatusChip tone="emerald">Entered</StatusChip>
        ) : (
          <>
            {item.payment_confirmed
              ? <StatusChip tone="emerald">Payment approved</StatusChip>
              : <StatusChip tone="slate">Awaiting approval</StatusChip>}
            {item.payment_type === "advance" && item.balance_due && (
              <StatusChip tone="amber">Balance {money(item.balance_due)}</StatusChip>
            )}
          </>
        )}
      </div>

      <SectionTitle>Customer</SectionTitle>
      <DetailRow label="Name" value={isNote ? item.customer_name : item.customer?.name} strong />
      <DetailRow label="Phone" value={isNote ? item.customer_phone : item.customer?.phone_number} />
      <DetailRow label="Location" value={isNote ? item.customer_location : item.customer?.location} />

      <SectionTitle>Car</SectionTitle>
      <DetailRow label="Car" value={car} />
      <DetailRow label="Variant" value={variantLabel(item.car_variant)} />
      <DetailRow label="Year" value={item.car_year} />

      <SectionTitle>{isNote ? "Parts sold" : "Products sold"}</SectionTitle>
      {isNote ? (
        (item.parts || []).length ? (
          <div className="flex flex-wrap gap-1.5 py-1">
            {item.parts.map((p, i) => (
              <span key={i} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">
                {p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ""}
              </span>
            ))}
          </div>
        ) : (
          <p className="py-1 text-sm font-semibold text-slate-400">No parts written on the note</p>
        )
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          {(item.items || []).map((line, i) => (
            <div key={line.id ?? i} className={`flex items-center justify-between gap-3 px-3 py-2 text-sm ${i % 2 ? "bg-slate-50/60" : "bg-white"}`}>
              <div className="min-w-0">
                <p className="truncate font-bold text-slate-800">
                  {line.name}{line.quantity > 1 ? ` ×${line.quantity}` : ""}
                </p>
                <p className="text-[11px] font-semibold text-slate-400">
                  {[
                    line.source ? sourceLabel(line.source) : null,
                    line.is_exchange ? (line.exchange_received ? "Exchange — old part received" : "Exchange — old part PENDING") : null,
                  ].filter(Boolean).join(" · ") || " "}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-black tabular-nums text-slate-900">{money(line.line_total)}</p>
                {line.quantity > 1 && line.unit_price != null && (
                  <p className="text-[11px] font-semibold text-slate-400">{money(line.unit_price)} each</p>
                )}
              </div>
            </div>
          ))}
          {!(item.items || []).length && (
            <p className="px-3 py-2 text-sm font-semibold text-slate-400">No products on this entry</p>
          )}
        </div>
      )}

      <SectionTitle>Money</SectionTitle>
      <DetailRow label={isNote ? "Amount" : "Total"} value={money(isNote ? item.amount : item.total_amount)} strong />
      <DetailRow
        label="Payment"
        value={item.payment_type === "advance"
          ? `Advance · ${money(item.advance_amount)} taken`
          : (item.payment_type ? "Full payment" : "")}
      />
      <DetailRow label="Mode" value={modeLabel(item.payment_mode)} />
      {isNote && <DetailRow label="Part source" value={sourceLabel(item.sale_source)} />}
      {!isNote && item.payment_type === "advance" && (
        <DetailRow
          label="Balance"
          value={item.balance_paid
            ? `Settled${item.balance_paid_mode ? ` · ${modeLabel(item.balance_paid_mode)}` : ""}`
            : (item.balance_due ? `${money(item.balance_due)} still to collect` : "—")}
        />
      )}
      {!isNote && item.payment_confirmed_at && (
        <DetailRow label="Approved at" value={item.payment_confirmed_at} />
      )}

      <SectionTitle>Record</SectionTitle>
      {isNote ? (
        <>
          <DetailRow label="Noted by" value={item.supervisor_name} />
          <DetailRow label="Sale date" value={item.sale_date} />
          {item.status === "entered" && (
            <DetailRow
              label="Entered"
              value={[item.entered_by, fmtStamp(item.entered_at)].filter(Boolean).join(" · ")}
            />
          )}
          <DetailRow label="Note" value={item.note} />
        </>
      ) : (
        <>
          <DetailRow label="Typed in by" value={item.entered_by_name} />
          <DetailRow label="Sold on" value={item.sold_at} />
          <DetailRow label="Notes" value={item.notes} />
        </>
      )}
      <DetailRow label="Created" value={fmtStamp(item.created_at)} />
      <DetailRow label="Last change" value={fmtStamp(item.updated_at)} />
    </Modal>
  );
};

// ── Edit a supervisor note ───────────────────────────────────────────────────
// Parts here are name+quantity only — the note never carried prices; the
// money lives in the single amount the supervisor wrote down.
const NoteEditModal = ({ note, brands, partNames, onClose, onSaved }) => {
  const [form, setForm] = useState({
    customer_name: note.customer_name || "",
    customer_phone: note.customer_phone || "",
    customer_location: note.customer_location || "",
    car_brand: note.car_brand || "",
    car_model: note.car_model || "",
    car_variant: note.car_variant || "",
    car_year: note.car_year || "",
    sale_date: note.sale_date || "",
    amount: note.amount != null ? String(Number(note.amount)) : "",
    payment_type: note.payment_type || "full",
    advance_amount:
      note.advance_amount != null ? String(Number(note.advance_amount)) : "",
    payment_mode: note.payment_mode || "",
    sale_source: note.sale_source || "cap",
    note: note.note || "",
    parts: (note.parts || []).map((p) => ({
      name: p.name || "",
      quantity: String(p.quantity || 1),
    })),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const setPart = (i, key, value) =>
    setForm((f) => ({
      ...f,
      parts: f.parts.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)),
    }));

  // Notes store the inquiry's variant CODES (dmt…), but older ones may carry
  // free text — an unknown value becomes its own option instead of being
  // silently replaced on the next save.
  const variantOptions = VARIANT_OPTIONS.some((o) => o.value === form.car_variant)
    ? VARIANT_OPTIONS
    : [...VARIANT_OPTIONS, { value: form.car_variant, label: form.car_variant }];

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const body = {
        ...form,
        advance_amount:
          form.payment_type === "advance" ? form.advance_amount : null,
        parts: form.parts
          .filter((p) => p.name.trim())
          .map((p) => ({ name: p.name.trim(), quantity: Number(p.quantity) || 1 })),
      };
      await saleRemindersApi.update(note.id, body);
      onSaved();
    } catch (e) {
      setError(errText(e, "Could not save the note."));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Correct this sale note"
      subtitle={`Noted by ${note.supervisor_name || "a supervisor"} — fix anything written down wrong.`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={submit} disabled={busy} className="rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-black text-white shadow-sm hover:bg-violet-700 disabled:opacity-60">
            {busy ? "Saving…" : "Save note"}
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </div>
      )}
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Customer name">
            <input className={inputCls} value={form.customer_name} onChange={(e) => set("customer_name")(e.target.value)} />
          </Field>
          <Field label="Phone">
            <input className={inputCls} value={form.customer_phone} onChange={(e) => set("customer_phone")(e.target.value)} />
          </Field>
          <Field label="Location">
            <input className={inputCls} value={form.customer_location} onChange={(e) => set("customer_location")(e.target.value)} />
          </Field>
        </div>

        <Field label="Car — type a few letters, pick from the catalogue">
          <CarPicker
            catalog={brands}
            brand={form.car_brand}
            model={form.car_model}
            onPick={(brand, model) => setForm((f) => ({ ...f, car_brand: brand, car_model: model }))}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Variant">
            <Select value={form.car_variant} onChange={set("car_variant")} options={variantOptions} />
          </Field>
          <Field label="Year">
            <ComboInput
              value={form.car_year}
              options={YEARS}
              onChange={set("car_year")}
              placeholder="e.g. 2019"
              numeric
            />
          </Field>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
            Parts sold — type or pick from the catalogue
          </p>
          {form.parts.length > 0 && (
            <div className="mb-1 flex gap-2 pr-11 text-[10px] font-bold uppercase tracking-wide text-slate-400">
              <span className="min-w-0 flex-1">Part</span>
              <span className="w-20 shrink-0">Qty</span>
            </div>
          )}
          <div className="space-y-2">
            {form.parts.map((p, i) => (
              <div key={i} className="flex gap-2">
                <div className="min-w-0 flex-1">
                  <ComboInput
                    value={p.name}
                    options={partNames}
                    onChange={(v) => setPart(i, "name", v)}
                    placeholder="Part name"
                  />
                </div>
                <input
                  className={`${inputCls} w-20`}
                  type="number"
                  min="1"
                  title="Quantity"
                  value={p.quantity}
                  onChange={(e) => setPart(i, "quantity", e.target.value)}
                />
                <button
                  onClick={() => setForm((f) => ({ ...f, parts: f.parts.filter((_, idx) => idx !== i) }))}
                  className="rounded-xl border border-slate-200 px-3 text-slate-400 hover:border-red-200 hover:text-red-500"
                  aria-label="Remove part"
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => setForm((f) => ({ ...f, parts: [...f.parts, { name: "", quantity: "1" }] }))}
              className="flex items-center gap-1.5 rounded-xl border border-dashed border-slate-300 px-3 py-2 text-xs font-bold text-slate-500 hover:border-violet-300 hover:text-violet-600"
            >
              <FiPlus /> Add a part
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Sale date">
            <input className={inputCls} type="date" value={form.sale_date} onChange={(e) => set("sale_date")(e.target.value)} />
          </Field>
          <Field label="Amount (₹)">
            <input className={inputCls} type="number" min="0" value={form.amount} onChange={(e) => set("amount")(e.target.value)} />
          </Field>
          <Field label="Payment">
            <Select
              value={form.payment_type}
              onChange={set("payment_type")}
              options={[
                { value: "full", label: "Full payment" },
                { value: "advance", label: "Advance" },
              ]}
            />
          </Field>
          {form.payment_type === "advance" && (
            <Field label="Advance (₹)">
              <input className={inputCls} type="number" min="0" value={form.advance_amount} onChange={(e) => set("advance_amount")(e.target.value)} />
            </Field>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mode">
            <Select value={form.payment_mode} onChange={set("payment_mode")} options={PAY_MODES} />
          </Field>
          <Field label="Part source">
            <Select value={form.sale_source} onChange={set("sale_source")} options={SOURCE_OPTIONS} />
          </Field>
        </div>
        <Field label="Note">
          <textarea rows={2} className={inputCls} value={form.note} onChange={(e) => set("note")(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
};

// ── Edit a counter sale ──────────────────────────────────────────────────────
// This is where "they sold 2 products but only 1 was typed in" gets fixed:
// items carry real prices, and the backend recomputes the total from them
// unless one is typed. A changed total lands in the price-change log with the
// reason given here.
const SaleEditModal = ({ sale, brands, partNames, exchangeNames, onClose, onSaved }) => {
  const [form, setForm] = useState({
    sold_at: sale.sold_at || "",
    car_brand: sale.car_brand || "",
    car_model: sale.car_model || "",
    car_variant: sale.car_variant || "",
    car_year: sale.car_year || "",
    payment_type: sale.payment_type === "advance" ? "advance" : "full",
    advance_amount:
      sale.advance_amount != null ? String(Number(sale.advance_amount)) : "",
    payment_mode: sale.payment_mode || "",
    notes: sale.notes || "",
    total_amount: "",   // blank = recompute from the items below
    reason: "",
    // EVERY item field rides along. Saving replaces the sale's items with
    // this list, so a field not carried here is a field silently erased —
    // exchange flags were once lost exactly this way.
    items: (sale.items || []).map((it) => ({
      name: it.name || "",
      quantity: String(it.quantity || 1),
      unit_price: it.unit_price != null ? String(Number(it.unit_price)) : "",
      source: it.source || "",
      is_exchange: Boolean(it.is_exchange),
      exchange_received: it.exchange_received !== false,
    })),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Set on a save attempt with unattributed products — every product still
  // missing its CAP/OSM tap shows red until it gets one.
  const [flagMissingSource, setFlagMissingSource] = useState(false);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const setItem = (i, key, value) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)),
    }));

  const itemsTotal = form.items.reduce(
    (sum, it) => sum + (Number(it.unit_price) || 0) * (Number(it.quantity) || 1),
    0,
  );

  const submit = async () => {
    setError("");
    const items = form.items
      .filter((it) => it.name.trim())
      .map((it) => ({
        name: it.name.trim(),
        quantity: Number(it.quantity) || 1,
        unit_price: it.unit_price === "" ? null : it.unit_price,
        source: it.source,
        is_exchange: Boolean(it.is_exchange),
        exchange_received: it.is_exchange ? it.exchange_received !== false : true,
      }));
    if (!items.length) {
      setError("A sale needs at least one product.");
      return;
    }
    // Same rule as the counter: no product saves unattributed. The backend
    // refuses blanks too — this just says it in words before the round trip.
    const missingSource = items.filter((it) => !it.source);
    if (missingSource.length) {
      setFlagMissingSource(true);
      setError(`Pick CAP or OSM for: ${missingSource.map((it) => it.name).join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const body = {
        sold_at: form.sold_at,
        car_brand: form.car_brand,
        car_model: form.car_model,
        car_variant: form.car_variant,
        car_year: form.car_year,
        items,
        total_amount: form.total_amount,   // '' → backend sums the items
        payment_type: form.payment_type,
        payment_mode: form.payment_mode,
        notes: form.notes,
        reason: form.reason,
      };
      if (form.payment_type === "advance") body.advance_amount = form.advance_amount;
      await counterApi.update(sale.id, body);
      onSaved();
    } catch (e) {
      setError(errText(e, "Could not save the sale."));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Correct this counter sale"
      subtitle={`${sale.customer?.name || "Customer"} · ${sale.customer?.phone_number || ""} — add what was missed or fix what was typed wrong.`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={submit} disabled={busy} className="rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-black text-white shadow-sm hover:bg-violet-700 disabled:opacity-60">
            {busy ? "Saving…" : "Save sale"}
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </div>
      )}
      <div className="space-y-4">
        <Field label="Car — type a few letters, pick from the catalogue">
          <CarPicker
            catalog={brands}
            brand={form.car_brand}
            model={form.car_model}
            onPick={(brand, model) => setForm((f) => ({ ...f, car_brand: brand, car_model: model }))}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field label="Sold on">
            <input className={inputCls} type="date" value={form.sold_at} onChange={(e) => set("sold_at")(e.target.value)} />
          </Field>
          <Field label="Variant">
            <Select value={form.car_variant} onChange={set("car_variant")} options={VARIANT_OPTIONS} />
          </Field>
          <Field label="Year">
            <ComboInput
              value={form.car_year}
              options={YEARS}
              onChange={set("car_year")}
              placeholder="e.g. 2019"
              numeric
            />
          </Field>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
            Products sold — type or pick, with the actual price taken
          </p>
          <div className="space-y-2.5">
            {form.items.map((it, i) => {
              const line = (Number(it.unit_price) || 0) * (Number(it.quantity) || 1);
              const received = it.exchange_received !== false;
              return (
                <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <ComboInput
                        value={it.name}
                        options={partNames}
                        onChange={(v) => setItem(i, "name", v)}
                        placeholder="Product name — type or pick"
                      />
                    </div>
                    <button
                      onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }))}
                      className="rounded-xl p-2.5 text-slate-300 transition hover:bg-rose-50 hover:text-rose-500"
                      aria-label="Remove product"
                      title="Remove this product"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <label className="block">
                      <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-400">Qty</span>
                      <input
                        className={inputCls}
                        type="number"
                        min="1"
                        value={it.quantity}
                        onChange={(e) => setItem(i, "quantity", e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-400">Price — ₹ each</span>
                      <input
                        className={inputCls}
                        type="number"
                        min="0"
                        placeholder="0"
                        value={it.unit_price}
                        onChange={(e) => setItem(i, "unit_price", e.target.value)}
                      />
                    </label>
                    <div>
                      <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-400">Line total</span>
                      <p className="px-1 py-2 text-sm font-black tabular-nums text-slate-900">
                        {it.unit_price === "" ? "—" : `₹${line.toLocaleString("en-IN")}`}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className={`text-[10px] font-bold uppercase tracking-wide ${flagMissingSource && !it.source ? "text-rose-500" : "text-slate-400"}`}>
                      From{flagMissingSource && !it.source ? " — pick one" : ""}
                    </span>
                    {[["cap", "CAP"], ["osm", "OSM"]].map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        title={v === "cap" ? "CAP's own stock" : "Open spare market"}
                        onClick={() => setItem(i, "source", it.source === v ? "" : v)}
                        className={`rounded-full border px-2.5 py-1 text-[10px] font-bold transition ${it.source === v ? "border-sky-300 bg-sky-50 text-sky-700" : flagMissingSource && !it.source ? "border-rose-300 bg-rose-50 text-rose-500 hover:border-rose-400" : "border-slate-200 bg-white text-slate-400 hover:border-sky-200 hover:text-sky-600"}`}
                      >
                        {l}
                      </button>
                    ))}
                    {/* Exchange is offered ONLY for the superadmin's Exchange
                        Parts list — plus any line already sold with one, so a
                        legacy flag stays visible (and undoable) even if the
                        list changed since. */}
                    {(it.is_exchange || isExchangeEligible(it.name, exchangeNames)) && (
                      <>
                        <span className="mx-1 h-4 w-px bg-slate-200" aria-hidden="true" />
                        <button
                          type="button"
                          title="Customer trades in their old part on this line"
                          onClick={() => setItem(i, "is_exchange", !it.is_exchange)}
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold transition ${it.is_exchange ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 bg-white text-slate-400 hover:border-violet-200 hover:text-violet-600"}`}
                        >
                          <FiRefreshCw className="h-3 w-3" />
                          {it.is_exchange ? "With exchange" : "No exchange"}
                        </button>
                        {it.is_exchange && (
                          <button
                            type="button"
                            title="Tap to flip whether the old part has been handed over"
                            onClick={() => setItem(i, "exchange_received", !received)}
                            className={`rounded-full border px-2.5 py-1 text-[10px] font-bold transition ${received ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}
                          >
                            {received ? "Old part received" : "Old part to collect"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            <button
              onClick={() => setForm((f) => ({ ...f, items: [...f.items, { name: "", quantity: "1", unit_price: "", source: "", is_exchange: false, exchange_received: true }] }))}
              className="flex items-center gap-1.5 rounded-xl border border-dashed border-slate-300 px-3 py-2 text-xs font-bold text-slate-500 hover:border-violet-300 hover:text-violet-600"
            >
              <FiPlus /> Add a product
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Charged total (₹)">
              <input
                className={inputCls}
                type="number"
                min="0"
                placeholder={itemsTotal ? itemsTotal.toLocaleString("en-IN") : "0"}
                value={form.total_amount}
                onChange={(e) => set("total_amount")(e.target.value)}
              />
            </Field>
            <Field label="Payment">
              <Select
                value={form.payment_type}
                onChange={set("payment_type")}
                options={[
                  { value: "full", label: "Full payment" },
                  { value: "advance", label: "Advance" },
                ]}
              />
            </Field>
            {form.payment_type === "advance" && (
              <Field label="Advance taken (₹)">
                <input className={inputCls} type="number" min="0" value={form.advance_amount} onChange={(e) => set("advance_amount")(e.target.value)} />
              </Field>
            )}
            <Field label="Paid by">
              <Select value={form.payment_mode} onChange={set("payment_mode")} options={PAY_MODES} />
            </Field>
          </div>
          <p className="mt-2 text-[11px] font-semibold leading-relaxed text-slate-400">
            The products above add up to <strong className="text-slate-600">₹{itemsTotal.toLocaleString("en-IN")}</strong>.
            Leave the charged total blank to charge exactly that — type a figure only when the bill was different.
          </p>
        </div>
        <Field label="Notes">
          <textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => set("notes")(e.target.value)} />
        </Field>
        <Field label="Why the correction? (kept in the price-change log if the money moves)">
          <input className={inputCls} placeholder="e.g. second product was missed at entry" value={form.reason} onChange={(e) => set("reason")(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
};

// ── One row of the register ──────────────────────────────────────────────────
const shortDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// The clock time a record was written down. The list is ordered by it, so it
// belongs on the row — an order you cannot see reads as no order at all.
// Two dialects again: notes send raw ISO, the counter book sends text that is
// already formatted ("05 Aug 2026 at 4:10 pm") — take the time off the end.
const shortTime = (stamp) => {
  if (!stamp) return "";
  const d = new Date(stamp);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
  }
  const parts = String(stamp).split(" at ");
  // "Jul 25, 2026 at 11:43 PM IST" → "11:43 PM"; the zone is always ours.
  return parts.length > 1 ? parts[1].replace(/\s+[A-Z]{2,4}$/, "") : "";
};

const itemsSummary = (item) => {
  const list = item.kind === "supervisor" ? item.parts || [] : item.items || [];
  if (!list.length) return "—";
  const first = `${list[0].name}${list[0].quantity > 1 ? ` ×${list[0].quantity}` : ""}`;
  return list.length > 1 ? `${first} +${list.length - 1} more` : first;
};

const IconButton = ({ title, onClick, danger, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    className={`rounded-lg p-2 transition ${
      danger
        ? "text-slate-300 hover:bg-rose-50 hover:text-rose-600"
        : "text-slate-400 hover:bg-violet-50 hover:text-violet-700"
    }`}
  >
    {children}
  </button>
);

const Row = ({ item, onView, onEdit, onDelete, onMark, busy }) => {
  const isNote = item.kind === "supervisor";
  const badge = KIND_BADGES[item.kind] || KIND_BADGES.supervisor;
  const name = isNote ? item.customer_name : item.customer?.name;
  const phone = isNote ? item.customer_phone : item.customer?.phone_number;
  const car = [item.car_brand, item.car_model, item.car_year].filter(Boolean).join(" ");
  const amount = isNote ? item.amount : item.total_amount;

  let status;
  if (isNote) {
    status = item.status === "pending" ? (
      <button
        type="button"
        disabled={busy}
        onClick={() => onMark(item, "entered")}
        title="Tick it off once it is in the day book"
        className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-amber-700 ring-1 ring-amber-200 transition hover:bg-emerald-50 hover:text-emerald-700 hover:ring-emerald-200 disabled:opacity-50"
      >
        <FiCheck className="h-3 w-3" /> Mark entered
      </button>
    ) : (
      <StatusChip tone="emerald">Entered</StatusChip>
    );
  } else if (item.payment_type === "advance" && item.balance_due) {
    status = <StatusChip tone="amber">Balance {money(item.balance_due)}</StatusChip>;
  } else if (item.payment_confirmed) {
    status = <StatusChip tone="emerald">Approved</StatusChip>;
  } else {
    status = <StatusChip tone="slate">Awaiting</StatusChip>;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-slate-100 px-4 py-3 transition hover:bg-slate-50/70">
      <div className="w-20 shrink-0">
        <p className="text-sm font-black tabular-nums text-slate-800">{shortDay(isNote ? item.sale_date : item.sold_at)}</p>
        {shortTime(item.created_at) && (
          <p className="text-[10px] font-bold tabular-nums text-slate-400">{shortTime(item.created_at)}</p>
        )}
        <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wide ring-1 ${badge.cls}`}>
          {badge.label}
        </span>
      </div>
      <div className="min-w-[9rem] flex-1">
        <p className="truncate text-sm font-bold text-slate-900">{name || "Customer"}</p>
        <p className="truncate text-xs font-semibold text-slate-400">{phone || "No phone"}</p>
      </div>
      <div className="hidden min-w-[8rem] flex-1 md:block">
        <p className="truncate text-sm font-semibold text-slate-600">{car || "—"}</p>
        <p className="truncate text-xs font-semibold text-slate-400">
          {isNote ? `by ${item.supervisor_name || "supervisor"}` : (item.entered_by_name ? `by ${item.entered_by_name}` : " ")}
        </p>
      </div>
      <div className="hidden min-w-[10rem] flex-1 xl:block">
        <p className="truncate text-sm font-semibold text-slate-600">{itemsSummary(item)}</p>
      </div>
      <div className="w-24 shrink-0 text-right">
        <p className="text-sm font-black tabular-nums text-slate-900">{money(amount)}</p>
      </div>
      <div className="w-32 shrink-0 text-right sm:text-left">{status}</div>
      <div className="flex shrink-0 items-center justify-end">
        <IconButton title="View" onClick={() => onView(item)}><FiEye className="h-4 w-4" /></IconButton>
        <IconButton title="Edit" onClick={() => onEdit(item)}><FiEdit3 className="h-4 w-4" /></IconButton>
        <IconButton title="Delete" danger onClick={() => onDelete(item)}><FiTrash2 className="h-4 w-4" /></IconButton>
      </div>
    </div>
  );
};

// ── The page ─────────────────────────────────────────────────────────────────

export default function SupervisorSaleRemindersPage() {
  const [period, setPeriod] = useState("all");   // OVERALL by default — the whole book
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");      // debounced copy of q
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewItem, setViewItem] = useState(null);
  const [editNote, setEditNote] = useState(null);
  const [editSale, setEditSale] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [marking, setMarking] = useState(false);
  // brands→models for the car picker, all_parts for the part pickers — the
  // same catalogue the Inquiry screen reads.
  const [catalog, setCatalog] = useState({ brands: [], all_parts: [] });
  // The Exchange Parts list — only these part types offer the exchange
  // toggle in the sale editor, same rule as the counter form.
  const [exchangeNames, setExchangeNames] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    counterApi.catalog()
      .then(({ data: payload }) => setCatalog({
        brands: payload?.brands || [],
        all_parts: payload?.all_parts || [],
      }))
      .catch(() => {});   // the pickers still take typed values
    counterApi.exchangeParts()
      .then(({ data: payload }) => setExchangeNames(activeExchangeNames(payload?.exchange_parts)))
      .catch(() => {});   // no list → no exchange toggles, nothing breaks
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = { kind, status };
      if (period === "custom") {
        if (fromDate) params.from = fromDate;
        if (toDate) params.to = toDate;
      } else {
        params.period = period;
      }
      if (search) params.q = search;
      const { data: payload } = await saleNotesApi.list(params);
      setData(payload);
    } catch (e) {
      setError(errText(e, "Could not load the sale notes."));
    } finally {
      setLoading(false);
    }
  }, [period, fromDate, toDate, kind, status, search]);

  useEffect(() => {
    load();
  }, [load]);

  const mark = async (item, next) => {
    setMarking(true);
    try {
      await saleRemindersApi.update(item.id, { status: next });
      // Keep an open view popup honest about the new status.
      setViewItem((v) => (v && v.kind === "supervisor" && v.id === item.id
        ? { ...v, status: next }
        : v));
      await load();
    } catch (e) {
      setError(errText(e, "Could not update the entry."));
    } finally {
      setMarking(false);
    }
  };

  const openEdit = (item) => {
    setViewItem(null);
    if (item.kind === "supervisor") setEditNote(item);
    else setEditSale(item);
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleteError("");
    try {
      if (confirmDelete.kind === "supervisor") {
        await saleRemindersApi.remove(confirmDelete.id);
      } else {
        await counterApi.remove(confirmDelete.id);
      }
      setConfirmDelete(null);
      load();
    } catch (e) {
      setDeleteError(errText(e, "Could not delete it."));
    }
  };

  const notes = data?.notes || [];

  return (
    <MainLayout>
      <div className="min-h-full bg-slate-50 p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-7xl">
          <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 p-6 text-white shadow-xl sm:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                  <FiCalendar className="h-6 w-6 text-violet-300" />
                </div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-300">
                  The direct-sales register
                </p>
                <h1 className="mt-2 text-3xl font-black tracking-tight">Sale Notes</h1>
                <p className="mt-2 max-w-xl text-sm leading-6 text-slate-300">
                  Everything sold outside the inquiry flow — the supervisors'
                  notes and the counter book, in one place.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <div className="rounded-2xl bg-white/10 px-5 py-4 ring-1 ring-white/10">
                  <p className="text-xs text-slate-300">Records</p>
                  <p className="mt-1 text-2xl font-black">{data?.count ?? "—"}</p>
                </div>
                <div className="rounded-2xl bg-white/10 px-5 py-4 ring-1 ring-white/10">
                  <p className="text-xs text-slate-300">Counter book</p>
                  <p className="mt-1 text-2xl font-black">{data ? money(data.book_total) : "—"}</p>
                </div>
                <div className="rounded-2xl bg-amber-400/15 px-5 py-4 ring-1 ring-amber-300/20">
                  <p className="text-xs text-amber-200">Awaiting entry</p>
                  <p className="mt-1 text-2xl font-black text-amber-300">
                    {data?.pending_count ?? "—"}
                    {data && Number(data.pending_total) > 0 && (
                      <span className="ml-2 text-sm font-bold">{money(data.pending_total)}</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* window chips + narrowing */}
          <div className="mt-6 space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              {WINDOWS.map((w) => (
                <button
                  key={w.value}
                  onClick={() => setPeriod(w.value)}
                  className={`rounded-xl px-4 py-2 text-xs font-bold uppercase tracking-wide transition ${
                    period === w.value
                      ? "bg-violet-600 text-white shadow-sm"
                      : "bg-slate-100 text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {w.label}
                </button>
              ))}
              {period === "custom" && (
                <div className="flex items-center gap-2">
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={`${inputCls} w-auto`} aria-label="From date" />
                  <span className="text-xs font-bold text-slate-400">to</span>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={`${inputCls} w-auto`} aria-label="To date" />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="flex flex-1 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4">
                <FiSearch className="text-violet-600" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search a customer name or phone…"
                  className="w-full bg-transparent py-3 text-sm font-bold text-slate-800 outline-none"
                />
              </label>
              <Select value={kind} onChange={setKind} options={KINDS} className="sm:w-48" />
              <div className="flex rounded-xl bg-slate-100 p-1" title="Supervisor notes' entry workflow">
                {["all", "pending", "entered"].map((x) => (
                  <button
                    key={x}
                    onClick={() => setStatus(x)}
                    className={`rounded-lg px-4 py-2 text-xs font-bold uppercase transition ${status === x ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                  >
                    {x}
                  </button>
                ))}
              </div>
              <button
                onClick={load}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
                aria-label="Refresh"
              >
                <FiRefreshCw className={loading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-24">
              <FiRefreshCw className="h-7 w-7 animate-spin text-violet-600" />
            </div>
          ) : notes.length === 0 ? (
            <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-white py-20 text-center">
              <FiCheckCircle className="mx-auto h-10 w-10 text-emerald-500" />
              <h2 className="mt-4 text-lg font-black text-slate-800">Nothing in this window</h2>
              <p className="mt-1 text-sm text-slate-500">
                Widen the window or clear the filters.
              </p>
            </div>
          ) : (
            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              {/* column headings — align with the row grid, fade out as
                  columns collapse on smaller screens */}
              <div className="hidden items-center gap-x-4 border-b border-slate-200 bg-slate-50/80 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-400 sm:flex">
                <span className="w-20 shrink-0">Date</span>
                <span className="min-w-[9rem] flex-1">Customer</span>
                <span className="hidden min-w-[8rem] flex-1 md:block">Car · by</span>
                <span className="hidden min-w-[10rem] flex-1 xl:block">Sold</span>
                <span className="w-24 shrink-0 text-right">Amount</span>
                <span className="w-32 shrink-0">Status</span>
                <span className="w-[6.5rem] shrink-0 text-right">Actions</span>
              </div>
              {notes.map((item) => (
                <Row
                  key={`${item.kind}-${item.id}`}
                  item={item}
                  busy={marking}
                  onView={setViewItem}
                  onEdit={openEdit}
                  onMark={mark}
                  onDelete={(x) => { setDeleteError(""); setConfirmDelete(x); }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {viewItem && (
        <ViewModal
          item={viewItem}
          busy={marking}
          onClose={() => setViewItem(null)}
          onEdit={openEdit}
          onMark={mark}
        />
      )}
      {editNote && (
        <NoteEditModal
          note={editNote}
          brands={catalog.brands}
          partNames={catalog.all_parts}
          onClose={() => setEditNote(null)}
          onSaved={() => { setEditNote(null); load(); }}
        />
      )}
      {editSale && (
        <SaleEditModal
          sale={editSale}
          brands={catalog.brands}
          partNames={catalog.all_parts}
          exchangeNames={exchangeNames}
          onClose={() => setEditSale(null)}
          onSaved={() => { setEditSale(null); load(); }}
        />
      )}
      {confirmDelete && (
        <DeleteConfirm
          title={confirmDelete.kind === "supervisor" ? "Delete this sale note?" : "Delete this counter sale?"}
          summary={
            confirmDelete.kind === "supervisor"
              ? `${confirmDelete.customer_name || "note"} — ${money(confirmDelete.amount)}`
              : `${confirmDelete.customer?.name || "sale"} — ${money(confirmDelete.total_amount)}`
          }
          consequences={
            confirmDelete.kind === "supervisor"
              ? ["The supervisor's note is removed for good"]
              : ["The sale and its products come out of the counter book",
                 "The day's takings change by this amount"]
          }
          error={deleteError}
          onClose={() => setConfirmDelete(null)}
          onConfirm={doDelete}
        />
      )}
    </MainLayout>
  );
}
